import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { CollapsibleSection } from './CollapsibleSection'
import {
  useCancelDeviceCommand,
  useControlCapabilities,
  useCreateDeviceCommand,
  useDeviceCommands,
} from '../hooks/useDeviceControl'
import type { ControlActuator, ControlMode, DownlinkCommand, DownlinkCommandStatus } from '../types/control'

interface Props {
  sigfoxId: string
}

const STATUS_STYLES: Record<DownlinkCommandStatus, string> = {
  QUEUED: 'bg-amber-100 text-amber-800',
  SENT: 'bg-blue-100 text-blue-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  SUPERSEDED: 'bg-gray-100 text-gray-600',
  EXPIRED: 'bg-gray-100 text-gray-600',
  FAILED: 'bg-red-100 text-red-800',
  CANCELED: 'bg-gray-100 text-gray-600',
}

function formatValue(mode: ControlMode, value: number): string {
  if (mode === 'BINARY') return value >= 1 ? 'Open' : 'Closed'
  if (mode === 'PERCENT') return `${value}% open`
  return `${value}°`
}

function formatTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function isInFlight(status: DownlinkCommandStatus): boolean {
  return status === 'QUEUED' || status === 'SENT'
}

/**
 * Remote-control panel for downlink-capable devices (gates/valves).
 *
 * Renders nothing unless the API reports the device controllable for this user
 * (owner / FULL_CONTROL). Control is asynchronous: a command is queued, served
 * to the device on its next ack poll (minutes-to-hours later), and confirmed by
 * a later uplink reading — the UI is a timeline, not a live switch.
 */
export function DeviceControlPanel({ sigfoxId }: Props) {
  const { data: caps, isLoading: capsLoading } = useControlCapabilities(sigfoxId)

  // Only render once we know the device is controllable for this user.
  if (capsLoading) return null
  if (!caps?.controllable || caps.actuators.length === 0) return null

  return (
    <CollapsibleSection title="Remote Control" open={false}>
      <div className="p-4 space-y-6">
        {caps.actuators.map((actuator) => (
          <ActuatorControl key={actuator.id} sigfoxId={sigfoxId} actuator={actuator} />
        ))}
        <p className="text-xs text-gray-500">
          Commands are delivered the next time the device checks in, then confirmed by its following
          sensor reading. This can take from minutes up to several hours depending on the device&apos;s
          schedule.
        </p>
      </div>
    </CollapsibleSection>
  )
}

function ActuatorControl({ sigfoxId, actuator }: { sigfoxId: string; actuator: ControlActuator }) {
  const { data: commands } = useDeviceCommands(sigfoxId)
  const createCommand = useCreateDeviceCommand(sigfoxId)
  const cancelCommand = useCancelDeviceCommand(sigfoxId)

  const mode = actuator.control_mode
  const min = actuator.min_value
  const max = actuator.max_value

  const channelCommands = useMemo(
    () => (commands ?? []).filter((c) => c.actuator_channel === actuator.channel),
    [commands, actuator.channel]
  )
  const pending = channelCommands.find((c) => isInFlight(c.status))
  const lastConfirmed = channelCommands.find((c) => c.status === 'CONFIRMED')

  const [target, setTarget] = useState<number>(lastConfirmed?.target_value ?? min)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const label = actuator.label || `Actuator ${actuator.channel}`

  const submit = async () => {
    try {
      await createCommand.mutateAsync({ targetValue: target, controlMode: mode, actuatorChannel: actuator.channel })
      toast.success(`Command queued: ${label} → ${formatValue(mode, target)}`, { duration: 10000 })
    } catch (err: any) {
      toast.error(`Failed to queue command: ${err?.message ?? 'unknown error'}`, { duration: 20000 })
    } finally {
      setConfirmOpen(false)
    }
  }

  const cancel = async (id: string) => {
    try {
      await cancelCommand.mutateAsync(id)
      toast.success('Command canceled', { duration: 8000 })
    } catch (err: any) {
      toast.error(`Could not cancel: ${err?.message ?? 'already sent'}`, { duration: 15000 })
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800">{label}</h3>
        <div className="text-sm text-gray-500">
          Confirmed: <span className="font-medium text-gray-700">
            {lastConfirmed ? formatValue(mode, lastConfirmed.target_value) : 'unknown'}
          </span>
        </div>
      </div>

      {/* Control affordance by mode */}
      <div className="mb-4">
        {mode === 'BINARY' && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTarget(1)}
              className={`px-4 py-2 rounded-lg ${target >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
            >
              Open
            </button>
            <button
              type="button"
              onClick={() => setTarget(0)}
              className={`px-4 py-2 rounded-lg ${target < 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
            >
              Closed
            </button>
          </div>
        )}

        {mode === 'PERCENT' && (
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={min}
              max={max}
              value={target}
              onChange={(e) => setTarget(Number(e.target.value))}
              className="flex-1"
            />
            <span className="w-16 text-right font-medium">{target}%</span>
          </div>
        )}

        {mode === 'ANGLE' && (
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={min}
              max={max}
              step={1}
              value={target}
              onChange={(e) => setTarget(Math.min(max, Math.max(min, Number(e.target.value))))}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <span className="text-gray-600">degrees ({min}–{max})</span>
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={createCommand.isPending}
        onClick={() => setConfirmOpen(true)}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Send command
      </button>

      {/* In-flight banner */}
      {pending && (
        <div className="mt-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-sm text-blue-900">
            Pending: <span className="font-medium">{formatValue(mode, pending.target_value)}</span>{' '}
            ({pending.status === 'QUEUED' ? 'waiting for device check-in' : 'delivered, awaiting confirmation'})
          </div>
          {pending.status === 'QUEUED' && (
            <button
              type="button"
              onClick={() => cancel(pending.id)}
              className="text-sm px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      <CommandTimeline mode={mode} commands={channelCommands.slice(0, 8)} />

      {confirmOpen && (
        <ConfirmModal
          message={`${label}: set to ${formatValue(mode, target)}? The change takes effect when the device next checks in.`}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={submit}
          isLoading={createCommand.isPending}
        />
      )}
    </div>
  )
}

function CommandTimeline({ mode, commands }: { mode: ControlMode; commands: DownlinkCommand[] }) {
  if (commands.length === 0) return null
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Recent commands</div>
      <ul className="space-y-1">
        {commands.map((c) => (
          <li key={c.id} className="flex items-center justify-between text-sm">
            <span className="text-gray-700">{formatValue(mode, c.target_value)}</span>
            <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLES[c.status]}`}>{c.status}</span>
            <span className="text-gray-400 text-xs">{formatTime(c.confirmed_at ?? c.sent_at ?? c.created_at)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ConfirmModal({
  message,
  onCancel,
  onConfirm,
  isLoading,
}: {
  message: string
  onCancel: () => void
  onConfirm: () => void
  isLoading: boolean
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Confirm command</h2>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg disabled:opacity-50"
          >
            {isLoading ? 'Sending…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  )
}
